// One leading "/" not followed by "/" or a backslash ("//host" is protocol-relative,
// "/\host" is normalised to it), and no backslash or control character anywhere
// else that URL parsing could turn into one of those.
const INTERNAL_PATH = /^\/(?![/\\])[^\\\p{Cc}]*$/u

/**
 * Whether `path` is a same-origin, path-only location that a browser or React
 * Router cannot reinterpret as an external URL.
 *
 * Use it for every navigation target that comes from a URL parameter (`?return=`,
 * `?next=`), where a crafted value such as `//evil.com`, `/\evil.com` or
 * `https://evil.com` would turn `navigate()` / `<Link>` into an open redirect
 * (React Router 6 advisories GHSA-jjmj-jmhj-qwj2 and GHSA-wrjc-x8rr-h8h6).
 *
 * @param path - Untrusted navigation target
 * @returns `true` when `path` is a plain same-origin path
 */
export function isSafeInternalPath(
  path: string | null | undefined,
): path is string {
  return !!path && INTERNAL_PATH.test(path)
}

/**
 * Returns `path` when {@link isSafeInternalPath} accepts it, `fallback` otherwise.
 *
 * @param path - Untrusted navigation target
 * @param fallback - Path to use when `path` is missing or not an internal path
 * @returns `path` when it is a plain same-origin path, `fallback` otherwise
 *
 * @example
 * ```typescript
 * toSafeInternalPath('/briefs/1?tab=share', '/briefs') // '/briefs/1?tab=share'
 * toSafeInternalPath('//evil.com', '/briefs') // '/briefs'
 * ```
 */
export function toSafeInternalPath(
  path: string | null | undefined,
  fallback: string,
): string {
  return isSafeInternalPath(path) ? path : fallback
}
